import {computed, ref, type Ref} from 'vue'
import * as d3 from 'd3'
import {DateTime} from 'luxon'
import type {ChartData} from '@/client/mentor/MessagesClient'
import {DATE_FMT, DATE_TIME_FMT, TIME_FMT, useDateTimeFormatter} from '@/composable/useDateTimeFormatter'

type TemporalLabelType = 'date' | 'time' | 'dateTime'

export interface HoverTooltipState {
  show: boolean
  x: number
  y: number
  title: string
  value: string
}

export interface ChartLegendItem {
  key: string
  label: string
  color: string
  isHidden: boolean
  value?: number
}

export interface PieChartLegendItem extends ChartLegendItem {
  value: number
}

export interface TooltipPayload {
  title: string
  value: string
}

const ASSISTANT_BRAND_CHART_COLORS = ['#052C4D', '#0079C2', '#97C2E6']
const LEGEND_FONT_SIZE = 11
const LEGEND_ROW_HEIGHT = 18
const LEGEND_ITEM_GAP = 16
const LEGEND_CIRCLE_RADIUS = 2.5
const LEGEND_ITEM_PADDING = 4
const LEGEND_CHAR_WIDTH = 6.4
const AXIS_TICK_FONT = '11px sans-serif'
const X_AXIS_LABEL_PADDING = 16
const X_AXIS_ROTATION_DEGREES = 45
const X_AXIS_ROTATED_MARGIN_PADDING = 25
const X_AXIS_ROTATED_FALLBACK_MARGIN = 72

const pixelsPerCharCache = new Map<string, number>()

// Roughly estimates the average pixel width of a character for a given font specification.
// Caches results for performance.
function estimatePixelsPerChar(fontSpec = AXIS_TICK_FONT): number {
  const cached = pixelsPerCharCache.get(fontSpec)
  if (cached !== undefined) {
    return cached
  }

  const ctx = document.createElement('canvas').getContext('2d')
  if (!ctx) {
    return LEGEND_CHAR_WIDTH
  }

  ctx.font = fontSpec
  const sampleText = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789.-() '
  const pixelsPerChar = ctx.measureText(sampleText).width / sampleText.length
  pixelsPerCharCache.set(fontSpec, pixelsPerChar)
  return pixelsPerChar
}

function estimateMaxLabelWidth(labels: string[], fontSpec = AXIS_TICK_FONT): number {
  if (!labels.length) {
    return 0
  }

  const maxCharCount = Math.max(...labels.map((label) => String(label).length))
  return maxCharCount * estimatePixelsPerChar(fontSpec)
}

export interface AssistantChartTheme {
  containerClass: string
  legendTextClass: string
  legendDisabledColor: string
  tooltipContainerClass: string
  tooltipTitleClass: string
  tooltipValueClass: string
  axisTextColor: string
  axisStrokeColor: string
  gridStrokeColor: string
  axisLabelColor: string
  pieCenterTextColor: string
  pieSliceStrokeColor: string
  seriesFallbackColor: string
}

const ASSISTANT_CHART_THEME: AssistantChartTheme = {
  containerClass: 'border-new-neutral-100 bg-new-neutral-50/30',
  legendTextClass: 'text-new-neutral-700',
  legendDisabledColor: '#AEAEAE',
  tooltipContainerClass: 'border-new-neutral-100 bg-new-neutral-0/95',
  tooltipTitleClass: 'text-new-neutral-800',
  tooltipValueClass: 'text-new-neutral-700',
  axisTextColor: '#4A4A4A',
  axisStrokeColor: '#C6C6C6',
  gridStrokeColor: '#E0E0E0',
  axisLabelColor: '#4A4A4A',
  pieCenterTextColor: '#4A4A4A',
  pieSliceStrokeColor: '#F8F8F8',
  seriesFallbackColor: ASSISTANT_BRAND_CHART_COLORS[0],
}

export function useAssistantMessageChart(chartData: Ref<ChartData>) {
  const [dateFormatter] = useDateTimeFormatter(DATE_FMT)
  const [timeFormatter] = useDateTimeFormatter(TIME_FMT)
  const [dateTimeFormatter] = useDateTimeFormatter(DATE_TIME_FMT)

  const chartContainer = ref<HTMLDivElement | null>(null)
  const hiddenLegendItems = ref<string[]>([])
  const hoverTooltip = ref<HoverTooltipState>({show: false, x: 0, y: 0, title: '', value: ''})

  const allSeries = computed(() => chartData.value.series.map((series) => series.name))

  const visibleSeries = computed(() => chartData.value.series.filter((series) => !hiddenLegendItems.value.includes(series.name)))

  const allCategories = computed(() =>
    Array.from(new Set(chartData.value.series.flatMap((series) => (series.points ?? []).map((point) => String(point.x)))))
  )

  const temporalLabelMetadata = computed(() => inferTemporalLabelMetadata(allCategories.value))

  const pieLegendItems = computed<PieChartLegendItem[]>(() => {
    const totals = new Map<string, number>()

    for (const series of chartData.value.series) {
      for (const point of series.points ?? []) {
        const numericValue = toNumericValue(point.y)
        if (numericValue === null) {
          continue
        }

        const label = String(point.x)
        totals.set(label, (totals.get(label) ?? 0) + numericValue)
      }
    }

    const pieColors = createAssistantChartPalette(totals.size)

    return Array.from(totals.entries()).map(([label, value], index) => ({
      key: label,
      label,
      value,
      color: pieColors[index],
      isHidden: hiddenLegendItems.value.includes(label),
    }))
  })

  const cartesianLegendItems = computed<ChartLegendItem[]>(() => {
    const cartesianColors = createAssistantChartPalette(allSeries.value.length)

    return allSeries.value.map((series, index) => ({
      key: series,
      label: series,
      color: cartesianColors[index],
      isHidden: hiddenLegendItems.value.includes(series),
    }))
  })

  function showTooltip(event: MouseEvent, title: string, value: string) {
    if (!chartContainer.value) {
      return
    }

    const bounds = chartContainer.value.getBoundingClientRect()
    hoverTooltip.value = {
      show: true,
      x: event.clientX - bounds.left + 12,
      y: event.clientY - bounds.top + 12,
      title,
      value,
    }
  }

  function hideTooltip() {
    hoverTooltip.value.show = false
  }

  function attachTooltipHandlers<Element extends d3.BaseType, Datum>(
    selection: d3.Selection<Element, Datum, any, any>,
    payloadResolver: (datum: Datum) => TooltipPayload
  ) {
    selection
      .on('mouseenter', (event, datum) => {
        const payload = payloadResolver(datum)
        showTooltip(event, payload.title, payload.value)
      })
      .on('mousemove', (event, datum) => {
        const payload = payloadResolver(datum)
        showTooltip(event, payload.title, payload.value)
      })
      .on('mouseleave', () => hideTooltip())

    return selection
  }

  function formatTooltipValue(value: number, unit?: string | null): string {
    const numeric = d3.format('~g')(value)
    return unit ? `${numeric}${unit}` : numeric
  }

  function toNumericValue(value: number | null | undefined): number | null {
    if (value === null || value === undefined) {
      return null
    }

    const numericValue = Number(value)
    return Number.isFinite(numericValue) ? numericValue : null
  }

  function toggleLegendItem(key: string) {
    hiddenLegendItems.value = hiddenLegendItems.value.includes(key)
      ? hiddenLegendItems.value.filter((item) => item !== key)
      : [...hiddenLegendItems.value, key]
  }

  function invertOtherLegendItems(activeKey: string, allKeys: string[]) {
    const hiddenSet = new Set(hiddenLegendItems.value)

    allKeys.forEach((key) => {
      if (key === activeKey) return

      if (hiddenSet.has(key)) {
        hiddenSet.delete(key)
      } else {
        hiddenSet.add(key)
      }
    })

    hiddenLegendItems.value = Array.from(hiddenSet)
  }

  function handleLegendClick(event: MouseEvent, key: string) {
    if (event.detail > 1) return
    toggleLegendItem(key)
  }

  function handleLegendDoubleClick(activeKey: string) {
    invertOtherLegendItems(activeKey, allSeries.value)
  }

  function handlePieLegendClick(event: MouseEvent, key: string) {
    if (event.detail > 1) return
    toggleLegendItem(key)
  }

  function handlePieLegendDoubleClick(activeKey: string) {
    invertOtherLegendItems(
      activeKey,
      pieLegendItems.value.map((item) => item.key)
    )
  }

  function formatXAxisLabel(label: string): string {
    const parsedDate = temporalLabelMetadata.value.parsedLabels.get(label)
    if (!parsedDate || !temporalLabelMetadata.value.type) {
      return label
    }

    if (temporalLabelMetadata.value.type === 'date') {
      return dateFormatter.value.format(parsedDate)
    }

    if (temporalLabelMetadata.value.type === 'dateTime') {
      return dateTimeFormatter.value.format(parsedDate)
    }

    return timeFormatter.value.format(parsedDate)
  }

  function getXAxisTickValues(categories: string[], plotWidth: number): {values: string[]; formattedLabels: string[]} {
    if (categories.length <= 2) {
      return {
        values: categories,
        formattedLabels: categories.map((category) => formatXAxisLabel(category)),
      }
    }

    const minTickSpacing = 72
    const maxTicks = Math.max(2, Math.floor(plotWidth / minTickSpacing))
    const step = Math.max(1, Math.ceil(categories.length / maxTicks))

    const sampledValues = categories.filter((_, index) => index % step === 0)
    const lastCategory = categories[categories.length - 1]
    if (sampledValues[sampledValues.length - 1] !== lastCategory) {
      sampledValues.push(lastCategory)
    }

    return {
      values: sampledValues,
      formattedLabels: sampledValues.map((category) => formatXAxisLabel(category)),
    }
  }

  function shouldRotateXAxisTickLabels(formattedLabels: string[], plotWidth: number): boolean {
    if (!formattedLabels.length) {
      return false
    }

    const slotWidth = plotWidth / formattedLabels.length

    return estimateMaxLabelWidth(formattedLabels) + X_AXIS_LABEL_PADDING > slotWidth
  }

  function getXAxisBottomMargin(formattedLabels: string[], shouldRotate: boolean, defaultBottom: number): number {
    if (!shouldRotate) {
      return defaultBottom
    }

    if (!formattedLabels.length) {
      return X_AXIS_ROTATED_FALLBACK_MARGIN
    }

    const radians = (X_AXIS_ROTATION_DEGREES * Math.PI) / 180
    const rotatedHeight = estimateMaxLabelWidth(formattedLabels) * Math.sin(radians)
    return Math.ceil(rotatedHeight + X_AXIS_ROTATED_MARGIN_PADDING)
  }

  function getLegendHeight(legendItems: ChartLegendItem[], availableWidth: number): number {
    if (!legendItems.length) {
      return 0
    }

    const wrappedRows = getLegendRows(legendItems, availableWidth)
    return Math.max(36, wrappedRows.length * LEGEND_ROW_HEIGHT)
  }

  function renderLegendInSvg(
    svg: d3.Selection<SVGSVGElement, unknown, null, undefined>,
    legendItems: ChartLegendItem[],
    margin: {top: number; right: number; bottom: number; left: number},
    legendTypeKey: 'cartesian' | 'pie',
    shouldRotateXAxis: boolean = false,
    legendHeight: number = 36
  ) {
    if (!legendItems.length) {
      return
    }

    const svgNode = svg.node()
    if (!svgNode) {
      return
    }

    const width = Number(svg.attr('width')) || 320
    const height = Number(svg.attr('height')) || 260
    const availableWidth = Math.max(0, width - margin.left - margin.right)
    const wrappedRows = getLegendRows(legendItems, availableWidth)

    const xAxisLabelY = height - legendHeight - (shouldRotateXAxis ? 8 : 12)
    const legendX = margin.left
    const legendY = xAxisLabelY + 14
    const legendGroup = svg.append('g').attr('class', 'legend').attr('transform', `translate(${legendX}, ${legendY})`)

    wrappedRows.forEach((row, rowIndex) => {
      let currentX = 0

      row.forEach((item) => {
        const itemWidth = estimateLegendItemWidth(item)
        const itemGroup = legendGroup
          .append('g')
          .attr('class', 'legend-item')
          .attr('transform', `translate(${currentX}, ${rowIndex * LEGEND_ROW_HEIGHT})`)
          .style('cursor', 'pointer')

        itemGroup.on('click', function (event: MouseEvent) {
          event.stopPropagation()
          if (legendTypeKey === 'cartesian') {
            handleLegendClick(event, item.key)
          } else {
            handlePieLegendClick(event, item.key)
          }
        })

        itemGroup.on('dblclick', function (event: MouseEvent) {
          event.stopPropagation()
          if (legendTypeKey === 'cartesian') {
            handleLegendDoubleClick(item.key)
          } else {
            handlePieLegendDoubleClick(item.key)
          }
        })

        itemGroup.append('title').text(item.isHidden ? 'Click to show' : 'Click to hide')

        itemGroup
          .append('circle')
          .attr('cx', 0)
          .attr('cy', 0)
          .attr('r', LEGEND_CIRCLE_RADIUS)
          .attr('fill', item.isHidden ? ASSISTANT_CHART_THEME.legendDisabledColor : item.color)

        itemGroup
          .append('text')
          .attr('x', LEGEND_ITEM_PADDING + LEGEND_CIRCLE_RADIUS * 2)
          .attr('y', 3)
          .attr('font-size', LEGEND_FONT_SIZE)
          .attr('fill', ASSISTANT_CHART_THEME.axisTextColor)
          .style('opacity', item.isHidden ? 0.45 : 1)
          .text(item.label)

        currentX += itemWidth
      })
    })
  }

  return {
    chartTheme: ASSISTANT_CHART_THEME,
    chartContainer,
    hiddenLegendItems,
    hoverTooltip,
    allSeries,
    visibleSeries,
    allCategories,
    cartesianLegendItems,
    pieLegendItems,
    showTooltip,
    hideTooltip,
    attachTooltipHandlers,
    formatTooltipValue,
    formatXAxisLabel,
    toNumericValue,
    handleLegendClick,
    handleLegendDoubleClick,
    handlePieLegendClick,
    handlePieLegendDoubleClick,
    getXAxisTickValues,
    shouldRotateXAxisTickLabels,
    getXAxisBottomMargin,
    getLegendHeight,
    renderLegendInSvg,
  }
}

function estimateLegendItemWidth(item: ChartLegendItem): number {
  const text = item.value !== undefined ? `${item.label}: ${item.value}` : item.label
  return LEGEND_ITEM_PADDING + LEGEND_CIRCLE_RADIUS * 2 + LEGEND_ITEM_PADDING + text.length * LEGEND_CHAR_WIDTH + LEGEND_ITEM_GAP
}

function getLegendRows(legendItems: ChartLegendItem[], availableWidth: number): ChartLegendItem[][] {
  if (!legendItems.length) {
    return []
  }

  const wrappedRows: ChartLegendItem[][] = []
  let currentRow: ChartLegendItem[] = []
  let currentRowWidth = 0

  for (const item of legendItems) {
    const itemWidth = estimateLegendItemWidth(item)
    if (currentRow.length > 0 && currentRowWidth + itemWidth > availableWidth) {
      wrappedRows.push(currentRow)
      currentRow = [item]
      currentRowWidth = itemWidth
      continue
    }

    currentRow.push(item)
    currentRowWidth += itemWidth
  }

  if (currentRow.length > 0) {
    wrappedRows.push(currentRow)
  }

  return wrappedRows
}

function createAssistantChartPalette(count: number): string[] {
  if (count <= 0) {
    return []
  }

  if (count <= ASSISTANT_BRAND_CHART_COLORS.length) {
    return ASSISTANT_BRAND_CHART_COLORS.slice(0, count)
  }

  const interpolate = d3.interpolateRgbBasis(ASSISTANT_BRAND_CHART_COLORS)

  return Array.from({length: count}, (_, index) => {
    const t = count === 1 ? 0 : index / (count - 1)
    return interpolate(t)
  })
}

function inferTemporalLabelMetadata(labels: string[]): {type: TemporalLabelType | null; parsedLabels: Map<string, Date>} {
  if (!labels.length) {
    return {type: null, parsedLabels: new Map()}
  }

  const dateLabels = parseTemporalLabels(labels, 'date')
  if (dateLabels) {
    return {type: 'date', parsedLabels: dateLabels}
  }

  const dateTimeLabels = parseTemporalLabels(labels, 'dateTime')
  if (dateTimeLabels) {
    return {type: 'dateTime', parsedLabels: dateTimeLabels}
  }

  const timeLabels = parseTemporalLabels(labels, 'time')
  if (timeLabels) {
    return {type: 'time', parsedLabels: timeLabels}
  }

  return {type: null, parsedLabels: new Map()}
}

function parseTemporalLabels(labels: string[], type: TemporalLabelType): Map<string, Date> | null {
  const parsedLabels = new Map<string, Date>()

  for (const label of labels) {
    const parsedDate = parseTemporalLabel(label, type)
    if (!parsedDate) {
      return null
    }

    parsedLabels.set(label, parsedDate)
  }

  return parsedLabels
}

function parseTemporalLabel(label: string, type: TemporalLabelType): Date | null {
  if (type === 'date') {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(label)) {
      return null
    }

    const parsedDate = DateTime.fromISO(label)
    return parsedDate.isValid ? parsedDate.toJSDate() : null
  }

  if (type === 'dateTime') {
    if (!/^\d{4}-\d{2}-\d{2}[T\s]\d{2}:\d{2}/.test(label)) {
      return null
    }

    const parsedDateTime = DateTime.fromISO(label)
    return parsedDateTime.isValid ? parsedDateTime.toJSDate() : null
  }

  const knownTimeFormats = ['HH:mm', 'HH:mm:ss', 'HH:mm:ss.SSS', 'h:mm a', 'h:mm:ss a']

  for (const format of knownTimeFormats) {
    const parsedTime = DateTime.fromFormat(label, format)
    if (parsedTime.isValid) {
      return parsedTime.toJSDate()
    }
  }

  return null
}
